/**
 * THE ONLY FILE IN THIS APP THAT IMPORTS `@turnkey/*`. `tests/structure.test.ts` (2) enforces it.
 *
 * ONE signing path, one place the stamper is chosen, one place a dead session is classified. Every rule below
 * was paid for once, and each is carried VERBATIM rather than re-derived: an interface-first rewrite of this
 * file has already lost three of them, and each loss surfaces only as a rejected signature.
 *
 * WHAT THIS FILE GUARANTEES, and it is the whole custody argument:
 *   the browser holds a NON-EXTRACTABLE P-256 session key inside the SDK's own IndexedDB keystore. No script,
 *   including ours, can read it out; our server never sees it; and therefore our backend can build a
 *   transaction and ASK for it to be signed, and can never sign one itself. That is enforced by construction,
 *   not by policy — which is why nothing in this app may ever accept, hold or forward a private key.
 */

import {
  StamperType,
  TurnkeyClient,
  WalletSource,
  getClientSignatureMessageForLogin,
} from "@turnkey/core";
import { encryptOtpCodeToBundle } from "@turnkey/crypto";
import bs58 from "bs58";

/** Both signing calls are bounded. A signing call that never settles is WORSE than one that fails: the UI sits
 *  on "Signing…" forever with nothing to act on, and the user's only move is to reload — mid-money-path. */
export const SIGN_TIMEOUT_MS = 20_000;

async function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      p,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${message} (${ms / 1000}s)`)), ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * THE SIGNING SESSION IS DEAD, AND WHICH WAY IT DIED.
 *
 * A first-class RESULT, not an error string, because the three dead-session shapes all present as an opaque
 * SDK failure and all need the same recovery — clear the session and re-run sign-in — while everything else
 * needs the opposite. Without the classification the Buy button fails IDENTICALLY on every press with a raw
 * vendor message the user can do nothing with.
 */
export class SignerDeadError extends Error {
  readonly kind: "expired" | "missing" | "key_gone";
  constructor(kind: SignerDeadError["kind"], message: string) {
    super(message);
    this.name = "SignerDeadError";
    this.kind = kind;
  }
}

/** The user closed the prompt. NOT a failure of ours, and it must never be reported as one. */
export class SignerDeclinedError extends Error {
  constructor() {
    super("you cancelled the signature");
    this.name = "SignerDeclinedError";
  }
}

// ── the client ────────────────────────────────────────────────────────────────────────────────────────

let client: TurnkeyClient | null = null;
let ready: Promise<TurnkeyClient> | null = null;
let orgId: string | null = null;

/**
 * The PARENT organization id, handed in from the auth config the app already read.
 *
 * It is a parameter and not a fetch on purpose: this file may not call the network (structure rule 1), and a
 * second read of `/auth/config` would be a second place the value could differ inside one bundle. A user's own
 * session carries their SUB-organization, so nothing here needs to know who is signing in — which is what makes
 * it usable before a brand-new user's sub-org exists.
 */
export function configureTurnkey(organizationId: string): void {
  if (orgId === organizationId) return;
  orgId = organizationId;
  // A changed org id invalidates the memoised client rather than being ignored.
  client = null;
  ready = null;
}

/** Lazy and memoised: importing this module must not spin up a client for a visitor who never signs in, and
 *  `init()` must happen exactly once. */
async function turnkey(): Promise<TurnkeyClient> {
  if (client) return client;
  const organizationId = orgId;
  if (organizationId === null) throw new Error("the signing client has not been configured");
  ready ??= (async () => {
    const c = new TurnkeyClient({ apiBaseUrl: "https://api.turnkey.com", organizationId });
    await c.init();
    client = c;
    return c;
  })();
  return ready;
}

/**
 * WEBCRYPTO NEEDS A SECURE CONTEXT, AND THERE IS NO FALLBACK.
 *
 * The SDK's stamper calls `crypto.subtle.exportKey` on EVERY key-creation path and `crypto.subtle.sign` on
 * every stamp. Over plain http `crypto.subtle` is undefined, so all of it fails as the SDK's generic "Failed
 * to create API key pair". An `externalKeyPair` fallback cannot rescue this — it needs the same
 * `crypto.subtle` it would be written to avoid — and a fallback that cannot succeed only changes which error
 * you get while hiding the real cause.
 *
 * The fix is the ORIGIN, not the code. This guard exists so that reaching the app over http says so plainly.
 */
export function assertSecureContext(): void {
  if (globalThis.isSecureContext && typeof globalThis.crypto?.subtle?.generateKey === "function") {
    return;
  }
  throw new Error(
    "signing needs a secure context — open the terminal over https (plain http has no crypto.subtle)",
  );
}

// ── the session ───────────────────────────────────────────────────────────────────────────────────────

/** Create the session keypair INSIDE the SDK's keystore and return only its public half.
 *
 *  WHOEVER BINDS THE SESSION MUST ALSO OWN THE KEY. Generating the pair ourselves and keeping the private half
 *  in a module variable binds the session to a public key the SDK has never seen: at stamping time its
 *  keystore lookup fails with "Key not found for publicKey: …" and signing is impossible. */
export async function createSessionKey(): Promise<string> {
  assertSecureContext();
  const c = await turnkey();
  return c.createApiKeyPair();
}

/** Seal the OTP code to the vendor's ephemeral key, BOUND to this browser's session public key. Two
 *  consequences: our server never sees the plaintext code, and the session it unlocks is usable only by this
 *  browser — so a compromised backend cannot replay someone's sign-in. */
export async function sealOtpCode(
  code: string,
  targetBundle: string,
  sessionPublicKey: string,
): Promise<string> {
  return encryptOtpCodeToBundle(code.trim(), targetBundle, sessionPublicKey);
}

/**
 * The login client signature.
 *
 * THREE ENCODING RULES WHOSE ONLY SYMPTOM IS A REJECTED SIGNATURE, each already paid for once:
 *   · the helper is called WITHOUT a session-key argument — it decodes the BOUND key out of the verification
 *     token, and that is the key the vendor expects to have signed. Passing our own makes the server reject it.
 *   · the signature is RAW 64-byte r‖s and never DER (70/71/72 bytes → "invalid signature length").
 *   · `clientSignature` is an OBJECT, not a bare hex string — a string makes the vendor fail to PARSE the
 *     request at all, surfacing as a proto syntax error pointing at the hex.
 */
export async function buildLoginSignature(
  verificationToken: string,
  sessionPublicKey: string,
): Promise<{ message: string; publicKey: string; scheme: string; signature: string }> {
  const { message, publicKey } = getClientSignatureMessageForLogin({ verificationToken });
  if (publicKey !== sessionPublicKey) {
    // The token was bound to a different browser or tab than the one holding the private half.
    throw new Error("this code was requested in another tab — start sign-in again here");
  }
  const c = await turnkey();
  const signature = await c.signWithApiKey({ message, publicKey });
  return { message, publicKey, scheme: "CLIENT_SIGNATURE_SCHEME_API_P256", signature };
}

/** Hand the session to the SDK, which persists it with a NON-EXTRACTABLE key in IndexedDB. UNTIL THIS HAPPENS
 *  the session is a string we were handed and threw away, and the Buy button authenticates perfectly and can
 *  never sign. */
export async function storeUserSession(sessionToken: string): Promise<void> {
  const c = await turnkey();
  await c.storeSession({ sessionToken });
}

/**
 * A REAL KEYSTORE CHECK, and the reason no `signable` boolean is ever persisted anywhere in this app.
 *
 * A flag written at sign-in survives session expiry, a cleared keystore and any deploy that changed how
 * sessions are stored. Trusting it is how you get a Buy button that fails at the signature — the one failure
 * that costs the user a round trip through a signing prompt to learn something the browser already knew.
 */
export async function hasSession(): Promise<boolean> {
  try {
    const c = await turnkey();
    return Boolean(await c.getSession());
  } catch {
    return false;
  }
}

/**
 * When the SIGNING session expires, in wall-clock ms — or `null` if unknown.
 *
 * This is the client's half of the two-clock fix. Our bearer and the signing session are the same LENGTH and
 * start at different INSTANTS, so a user can mint a 6 h bearer five minutes before their signing session dies
 * and then spend 5 h 55 m in a state where every quote works, every build succeeds and every signature fails.
 * Reporting it at `/auth/session` lets the server CLAMP; a `null` here simply means no clamp is possible.
 *
 * The field name differs across SDK versions, so it is read defensively rather than asserted: a wrong guess
 * would over-claim an expiry and log people out early, which is why an unrecognised shape is `null` (no clamp)
 * rather than a fabricated number.
 */
export async function signerExpiresAt(): Promise<number | null> {
  try {
    const c = await turnkey();
    const session = (await c.getSession()) as unknown as Record<string, unknown> | null;
    if (!session) return null;
    for (const key of ["expiry", "exp", "expiresAt", "expirationSeconds"]) {
      const v = session[key];
      if (typeof v === "number" && Number.isFinite(v) && v > 0) {
        // Seconds or milliseconds, distinguished by magnitude: a seconds value is ~1.7e9 and a ms value ~1.7e12.
        return v < 1e11 ? v * 1000 : v;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Signing out locally must never fail loudly — the user asked to leave. */
export async function clearSigningSession(): Promise<void> {
  try {
    const c = await turnkey();
    await c.logout();
  } catch {
    /* deliberately swallowed */
  }
}

// ── signing ───────────────────────────────────────────────────────────────────────────────────────────

/** The account shape the SDK needs for an embedded wallet: `source` and `address`, nothing more.
 *
 *  We deliberately do NOT call `fetchWallets` first. Reading the SDK's own `signTransaction` shows it uses
 *  exactly those two fields and passes the address straight through as `signWith` — so the listing round trip
 *  bought nothing, and it FAILED on its own with a bare "Failed to fetch wallets", blocking signing for a
 *  reason that had nothing to do with signing. If the address is wrong the vendor rejects the signature, which
 *  is a better failure than never getting to attempt one. */
type WalletAccountArg = Parameters<TurnkeyClient["signTransaction"]>[0]["walletAccount"];

function embeddedAccount(address: string): WalletAccountArg {
  return { source: WalletSource.Embedded, address } as unknown as WalletAccountArg;
}

/**
 * Sign a MESSAGE with the wallet's Solana account → a base58 ed25519 signature.
 *
 * This is what proves wallet ownership to our own API. It is NOT a transaction: it moves nothing, and the
 * challenge text says so — a signing prompt that shows an opaque blob trains users to approve opaque blobs.
 *
 * The encoding is the fiddly part and getting it wrong fails as a signature that simply does not verify, with
 * no error: Solana is ed25519 over the RAW bytes, so the vendor must NOT pre-hash
 * (`HASH_FUNCTION_NOT_APPLICABLE`), the payload goes as hex, and base58 of r‖s is what a verifier expects.
 *
 * ⚠️ PASS THE MESSAGE RAW. `signMessage` DOES THE UTF-8 ENCODING ITSELF — do not hand it hex.
 *
 * `@turnkey/core`'s `signMessage` runs `toUtf8Bytes(message)` on whatever it is given and only THEN applies
 * `encoding` (`__clients__/core.mjs`, the embedded-wallet branch). Handing it `utf8ToHex(message)` therefore
 * makes it sign the ASCII of the hex STRING — 528 bytes of "4b696e65…" instead of the 264-byte message — and
 * the result is a perfectly valid 64-byte signature over the wrong bytes. There is no error anywhere: the
 * vendor succeeds, the length check passes, and the server rejects it as `bad_signature`.
 *
 * MEASURED 2026-08-15 against the live vendor with the company wallet: pre-hexed ❌, raw ✅. This is why
 * sign-in did not work on this project for as long as it did: the pre-hexed form looks correct precisely
 * because hex conversion is the thing the rule above tells you to do. That
 * rule belongs to `signRawPayload`, which takes an ALREADY-encoded payload; `signMessage` wraps it and adds
 * the encoding step. Two functions, one of which does it for you.
 */
export async function signSolanaMessage(
  subOrgId: string,
  address: string,
  message: string,
): Promise<string> {
  const c = await turnkey();
  const res = await withTimeout(
    c.signMessage({
      message,
      walletAccount: embeddedAccount(address),
      encoding: "PAYLOAD_ENCODING_HEXADECIMAL" as never,
      hashFunction: "HASH_FUNCTION_NOT_APPLICABLE" as never,
      // EXPLICIT, for the same reason as the transaction path below.
      stampWith: StamperType.ApiKey,
      organizationId: subOrgId,
    }),
    SIGN_TIMEOUT_MS,
    "the signer did not respond",
  ).catch(reclassify);

  const sig = hexToBytes(`${res.r}${res.s}`);
  // A LOCAL ASSERTION, not a server's. A DER-shaped signature is 70–72 bytes and would be rejected later, far
  // from the cause; 64 is the only length an ed25519 verifier accepts.
  if (sig.length !== 64) throw new Error(`expected a 64-byte signature, got ${sig.length}`);
  return bs58.encode(sig);
}

/**
 * Sign a base64 Solana transaction. Returns the SIGNED transaction, base64.
 *
 * ⚠️ THIS SIGNS WHATEVER IT IS HANDED. The caller is responsible for having run the pre-sign guard first,
 * because a signature is the last point at which anything can be checked. `auth/presign.ts` is that guard and
 * `trade/executeSwap.ts` is the one sequence that calls both in order.
 *
 * `stampWith` is EXPLICIT. Left unset, the client picks a stamper by its own resolution order and can land on
 * the PASSKEY stamper — and with no passkey registered that waits on a WebAuthn prompt which never appears, so
 * the call HANGS instead of failing. Serving the app over https makes an unset stamper MORE dangerous, not
 * less: the passkey path becomes genuinely selectable.
 *
 * The vendor takes the transaction as HEX and Jupiter hands us base64. The SDK passes `unsignedTransaction`
 * through untouched, so converting is our job, and getting it wrong surfaces only as a generic "Failed to sign
 * transaction" with the real cause swallowed. Convert in, convert out.
 */
export async function signSolanaTransaction(
  subOrgId: string,
  address: string,
  unsignedTransactionBase64: string,
): Promise<string> {
  const c = await turnkey();
  const signedHex = await withTimeout(
    c.signTransaction({
      walletAccount: embeddedAccount(address),
      unsignedTransaction: base64ToHex(unsignedTransactionBase64),
      transactionType: "TRANSACTION_TYPE_SOLANA",
      stampWith: StamperType.ApiKey,
      organizationId: subOrgId, // the signing key lives in the USER's sub-org
    }),
    SIGN_TIMEOUT_MS,
    "the signer did not respond",
  ).catch(reclassify);
  return hexToBase64(signedHex);
}

/**
 * Turn an opaque SDK failure into something a caller can branch on.
 *
 * The SDK's error wrapper REPLACES the message with a fixed string and keeps the real one on `cause`/`details`,
 * so "Failed to sign transaction" on its own is unactionable for a user and for us. Dig it out, then classify:
 *
 *   API_KEY_EXPIRED   the session aged out. Every session eventually does, whatever its length.
 *   NO_SESSION_FOUND  there is no session at all — reached most often AFTER one of the others cleared one,
 *                     while the app still believed it was signed in.
 *   "Key not found"   the session references a keypair this browser no longer holds (a cleared keystore).
 *
 * All three CLEAR THE SESSION here, so the UI drops to signed-out and says what to do rather than leaving a
 * button that fails identically on every press.
 */
async function reclassify(e: unknown): Promise<never> {
  const err = e as { message?: string; cause?: unknown; details?: unknown; code?: unknown };
  const cause =
    err?.cause instanceof Error
      ? err.cause.message
      : typeof err?.cause === "string"
        ? err.cause
        : err?.details
          ? JSON.stringify(err.details).slice(0, 300)
          : undefined;
  const detail = `${err?.message ?? "signing failed"}${cause ? `: ${cause}` : ""}`;

  // ⚠️ MATCH THE CODES, NOT THE PROSE. These were prose-only until 2026-08-17 and a real session death
  // slipped straight through: Turnkey returns `turnkeyErrorCode: PUBLIC_KEY_NOT_FOUND` alongside the
  // sentence "could not find public key in organization or its parent organization", and the pattern here
  // was `key not found` — the same three words in a different order, so it did not match.
  //
  // THE CONSEQUENCE WAS NOT A BAD MESSAGE, IT WAS A WEDGE. Falling past this block skips
  // `clearSigningSession()`, so the dead key stays in storage and EVERY SUBSEQUENT ATTEMPT FAILS IDENTICALLY
  // until the user finds Sign Out unaided. A classifier that misses does not degrade to "unclassified", it
  // degrades to "permanently broken with no way back" — and the user is shown a 300-character vendor string
  // containing an organizationId and a public key, which tells them nothing they can act on.
  //
  // Turnkey's error CODES are a stable enumeration; its sentences are not. Codes first, prose kept only as a
  // fallback for older SDK paths that surface no code.
  const expired = /api_key_expired|expired api key|SESSION_EXPIRED/i.test(detail);
  const missing = /no_session_found|no active session|SESSION_NOT_FOUND/i.test(detail);
  const keyGone =
    /PUBLIC_KEY_NOT_FOUND|key not found|could not find public key|public key could not be found/i.test(
      detail,
    );
  if (expired || missing || keyGone) {
    await clearSigningSession();
    throw new SignerDeadError(
      expired ? "expired" : missing ? "missing" : "key_gone",
      expired
        ? "your signing session expired — sign in again"
        : missing
          ? "there is no signing session on this device — sign in again"
          : "your signing session is no longer valid (its key is gone) — sign in again",
    );
  }
  if (/denied|declin|reject|cancel|abort/i.test(detail)) throw new SignerDeclinedError();
  throw new Error(`${detail}${err?.code ? ` [${String(err.code)}]` : ""}`);
}

// ── encodings ─────────────────────────────────────────────────────────────────────────────────────────

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.startsWith("0x") ? hex.slice(2) : hex;
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function base64ToHex(b64: string): string {
  const bin = atob(b64);
  let out = "";
  for (let i = 0; i < bin.length; i++) out += bin.charCodeAt(i).toString(16).padStart(2, "0");
  return out;
}

function hexToBase64(hex: string): string {
  const clean = hex.startsWith("0x") ? hex.slice(2) : hex;
  // If the vendor ever returns base64 already, passing it through here would CORRUPT it — detect the non-hex
  // case rather than silently producing garbage that an RPC rejects with an opaque error.
  if (!/^[0-9a-fA-F]*$/.test(clean) || clean.length % 2 !== 0) return hex;
  let bin = "";
  for (let i = 0; i < clean.length; i += 2) {
    bin += String.fromCharCode(Number.parseInt(clean.slice(i, i + 2), 16));
  }
  return btoa(bin);
}
